/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Form, Row, Space } from 'antd';
import AutomationsForm from '../Forms/AutomationsForm';
import PredefinedResponsesForm from '../Forms/PredefinedResponsesForm';
import SideModal from '../Layout/SideModal';

// Misc imports
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG } from '../../constants/main';
import { useCreateAutomationMutation, useCreatePredefinedResponseMutation, useUpdateAutomationMutation, useUpdatePredefinedResponseMutation } from '../../store/predefinedEntities/predefinedEntitiesApi';
import { useEffect, useMemo } from 'react';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';

// Constant declarations
const MODES = {
  RESPONSES: 'responses',
  AUTOMATIONS: 'automations'
};
const LIVECHAT_PERMISSIONS = [PERMISSIONS_ORG.ADMIN, ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN];

const checkConditions = (item) => {
  let conditions = [];

  if (item) {
    if (item.runOnMissedRequest) {
      conditions.push('runOnMissedRequest');
    }
    if (item.runOnEndConversation) {
      conditions.push('runOnEndConversation');
    }
  }
  return conditions;
};

const PredefinedEntityModal = ({
  open = false,
  setIsOpen,
  selectedItem = undefined,
  setSelectedItem,
  mode = MODES.RESPONSES,
  orgLevelForm = false
}) => {
  const { orgId } = useParams();
  const [form] = Form.useForm();
  const userPermissions = useUserPermissions(orgId);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const hasAdminRights = LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !selectedItem?.organizationWide;

  const [, { isLoading: isUpdatingPredefinedResponse }] = useUpdatePredefinedResponseMutation({ fixedCacheKey: 'updatePredefinedResponse' });
  const [, { isLoading: isCreatingPredefinedResponse }] = useCreatePredefinedResponseMutation({ fixedCacheKey: 'createPredefinedResponse' });
  const [, { isLoading: isUpdatingAutomation }] = useUpdateAutomationMutation({ fixedCacheKey: 'updateAutomation' });
  const [, { isLoading: isCreatingAutomation }] = useCreateAutomationMutation({ fixedCacheKey: 'createAutomation' });
  const { currentData: activeConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });

  const initialValues = useMemo(() => {
    if (selectedItem && selectedItem.id) {
      if (mode === MODES.RESPONSES) {
        return selectedItem?.id ? {
          responseName: selectedItem.name,
          responseMessage: selectedItem.text,
          categories: selectedItem.categories || [],
          organizationWide: selectedItem.organizationWide,
          tenantIds: selectedItem?.tenantIds || []
        } : {
          responseName: '',
          responseMessage: '',
          categories: [],
          organizationWide: false,
          tenantIds: orgLevelForm ? [] : [activeConversationInfo?.tenantId]
        };
      } else if (mode === MODES.AUTOMATIONS) {
        return selectedItem?.id ? {
          automationName: selectedItem ? selectedItem.name : '',
          automationCallbackUrl: selectedItem ? selectedItem.url : '',
          categories: selectedItem ? selectedItem.categories : [],
          automationHeaders: selectedItem ? selectedItem.headers : [],
          agentInput: selectedItem ? selectedItem.agentInput : [],
          organizationWide: selectedItem ? selectedItem.organizationWide : false,
          runOn: checkConditions(selectedItem),
          tenantIds: selectedItem ? selectedItem.tenantIds || [] : orgLevelForm ? [] : [activeConversationInfo?.tenantId]
        } : {
          automationName: '',
          automationCallbackUrl: '',
          categories: [],
          automationHeaders: [],
          agentInput: [],
          organizationWide: false,
          runOn: [],
          tenantIds: []
        };
      }
    } else {
      if (mode === MODES.RESPONSES) {
        return {
          responseName: '',
          responseMessage: '',
          categories: [],
          organizationWide: false,
          tenantIds: orgLevelForm ? [] : [activeConversationInfo?.tenantId]
        };
      } else if (mode === MODES.AUTOMATIONS) {
        return {
          automationName: '',
          automationCallbackUrl: '',
          categories: [],
          automationHeaders: [],
          agentInput: [],
          organizationWide: false,
          runOn: [],
          tenantIds: []
        };
      }
    }
  }, [selectedItem, mode]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues]);

  const renderForm = () => {
    if (mode === MODES.RESPONSES) {
      return (
        <PredefinedResponsesForm
          orgFormInstance={form}
          selectedItem={selectedItem}
          setSelectedResponse={setSelectedItem}
          hasAdminRights={hasAdminRights}
          setIsModalOpen={setIsOpen}
          orgLevelForm={orgLevelForm}
          initialValues={initialValues}
        />
      );
    } else if (mode === MODES.AUTOMATIONS) {
      return (
        <AutomationsForm
          form={form}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          hasAdminRights={hasAdminRights}
          setIsModalOpen={setIsOpen}
          orgLevelForm={orgLevelForm}
          initialValues={initialValues}
        />
      );
    }
  };

  return (
    <SideModal
      open={open}
      title={
        selectedItem
          ? `${t('Edit')} ${mode === MODES.RESPONSES ? t('Response') : t('Automation')}`
          : `${t('Create')} ${mode === MODES.RESPONSES ? t('New Response') : t('New Automation')}`
      }
      footer={
        <Row justify="end">
          <Col>
            <Space>
              <Button
                size="large"
                onClick={() => setIsOpen(false)}
                disabled={!hasAdminRights}
              >
                {t('Cancel')}
              </Button>

              <Button
                size="large"
                onClick={() => {
                  if (mode === MODES.AUTOMATIONS && form.getFieldValue('runOn').length) {
                    form.setFieldValue('agentInput', []);
                  }
                  form.submit();
                }}
                type="primary"
                loading={isCreatingPredefinedResponse || isUpdatingPredefinedResponse || isCreatingAutomation || isUpdatingAutomation}
                disabled={!hasAdminRights}
              >
                {!selectedItem ? t('Create Automation') : t('Save Changes')}
              </Button>
            </Space>
          </Col>
        </Row>
      }
      onClose={() => setIsOpen(false)}
      afterClose={() => {
        setSelectedItem();

      }}
      destroyOnClose
    >
      {renderForm()}
    </SideModal>
  );
};

PredefinedEntityModal.propTypes = {
  open: PropTypes.bool,
  setIsOpen: PropTypes.func,
  selectedItem: PropTypes.object,
  setSelectedItem: PropTypes.func,
  mode: PropTypes.string,
  orgLevelForm: PropTypes.bool
};

export default PredefinedEntityModal;
